import { createEffect, createEvent, createStore, sample } from 'effector'
import { IShipment } from 'src/shared/contract/shipment'
import { parseCookies, setCookie } from 'nookies'
import { useEvent, useStore } from 'effector-react'
import { getAddressById } from './api'
import { $stocks } from '@entities/stocks/model'
import { IStock } from '@entities/stocks/contract'
import { subscribeLoggerEffect } from 'src/infastruct/effector-utils/subscribe-sentry'
import { Done, persist, StorageAdapter } from 'src/shared/lib/effector-storage'

const KEY_SHIPMENT_STORAGE = 'storage-shipment'

export const $shipment = createStore<IShipment>({
    cityId: 1,
    shipAddressId: 0,
    shipAddressTitle: '',
    shipAddressCityId: 1,
    stockTitle: '',
    stockCityId: 1,
    stockId: 0
})

export const changeStock = createEvent<IShipment>()
export const changeCity = createEvent<number>()
export const changeDelivery = createEvent<IShipment>()
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const doneEvent = createEvent<Done<any>>()
export const initShipment = createEvent<IShipment>()

sample({
    clock: initShipment,
    target: $shipment
})

const changeDeliveryFx = createEffect<
    {
        source: IShipment
        clock: IShipment
    },
    IShipment,
    Error
>({
    handler: async ({ source, clock }): Promise<IShipment> => {
        if (!clock.shipAddressId) throw new Error(`Ошибка выбора адреса несуществующий id ${clock}`)

        if (clock.shipAddressTitle)
            return {
                ...source,
                cityId: clock.shipAddressCityId || 1,
                shipAddressId: clock.shipAddressId,
                shipAddressTitle: clock.shipAddressTitle,
                shipAddressCityId: clock.shipAddressCityId || 1
            }
        const address = await getAddressById(clock.shipAddressId)
        return {
            ...source,
            cityId: clock.shipAddressCityId || 1,
            shipAddressId: address.id,
            shipAddressTitle: address.address,
            shipAddressCityId: clock.shipAddressCityId || 1
        }
    }
})

subscribeLoggerEffect(changeDeliveryFx)

function regionNormalize(shipment: IShipment) {
    if (!shipment || !shipment.cityId) return shipment
    if (shipment?.stockId && shipment.stockCityId !== shipment.cityId) {
        shipment.stockId = 0
        shipment.stockTitle = ''
        shipment.stockCityId = 1
    }
    if (shipment?.shipAddressCityId && shipment.shipAddressCityId !== shipment.cityId) {
        shipment.shipAddressId = 0
        shipment.shipAddressTitle = ''
        shipment.shipAddressCityId = 1
    }

    return shipment
}

sample({
    clock: changeStock,
    source: [$shipment, $stocks],
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    fn: ([shipment, stocks]: [IShipment, IStock[]], clock): IShipment => {
        if (!clock.stockId) throw new Error(`Error change stock: incorrect select stock clock: ${clock}`)
        if (clock.stockTitle)
            return regionNormalize({
                ...shipment,
                cityId: clock.cityId,
                stockCityId: clock.cityId || 1,
                stockId: clock.stockId,
                stockTitle: clock.stockTitle
            })
        if (!stocks || stocks.length <= 0) throw new Error(`Error change stock:stocks entity is empty`)
        const selectedStock = stocks.find((x) => x.id === clock.stockId)
        if (!selectedStock || !selectedStock.id) throw new Error(`Error change stock: stock not find clock: ${clock}`)
        return regionNormalize({
            ...shipment,
            cityId: clock.cityId,
            stockId: clock.stockId,
            stockCityId: clock.cityId || 1,
            stockTitle: selectedStock.title
        })
    },
    target: $shipment
})

sample({
    clock: changeCity,
    filter: ({ shipment }, cityId) => Number.isInteger(cityId) && shipment.cityId !== cityId,
    source: { shipment: $shipment },
    fn: ({ shipment }, cityId) => {
        const newShipment = {
            ...shipment,
            cityId: cityId
        }
        return regionNormalize(newShipment)
    },
    target: $shipment
})

sample({
    clock: changeDelivery,
    source: $shipment,
    fn: (source, clock) => ({ source, clock }),
    target: changeDeliveryFx
})

sample({
    clock: changeDeliveryFx.doneData,
    fn: regionNormalize,
    target: $shipment
})

const adapter: StorageAdapter = <IShipment>() => {
    return {
        get() {
            const cookies = parseCookies()
            const shipmentStorage = cookies[KEY_SHIPMENT_STORAGE]
            if (!shipmentStorage) return shipmentStorage
            return JSON.parse(shipmentStorage)
        },

        set(value: IShipment) {
            setCookie(null, KEY_SHIPMENT_STORAGE, JSON.stringify(value), {
                maxAge: 365 * 24 * 60 * 60,
                path: '/'
            })
        }
    }
}

persist({ key: KEY_SHIPMENT_STORAGE, store: $shipment, adapter, done: doneEvent })

export const useShipment = () => useStore($shipment)
export const useChangeStock = () => useEvent(changeStock)
export const useChangeCity = () => useEvent(changeCity)
export const useChangeDelivery = () => useEvent(changeDelivery)
